import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Toast,
  TOAST_DEFAULT_MESSAGE,
  TOAST_VARIANT_LABELS,
  TOAST_VARIANTS,
  type ToastVariant,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ToastShowcase.css'

const ToastShowcase: React.FC = () => {
  const [message, setMessage] = useState(TOAST_DEFAULT_MESSAGE)
  const [highlightedVariant, setHighlightedVariant] =
    useState<ToastVariant>('info')
  const [darkMode, setDarkMode] = useState(false)
  const rowRefs = useRef<Partial<Record<ToastVariant, HTMLDivElement | null>>>(
    {},
  )

  useEffect(() => {
    rowRefs.current[highlightedVariant]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedVariant])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'toast-highlight-variant',
            label: 'Highlighted variant',
            control: (
              <select
                id="toast-highlight-variant"
                className="ui-field-control"
                value={highlightedVariant}
                onChange={(event) =>
                  setHighlightedVariant(event.target.value as ToastVariant)
                }
              >
                {TOAST_VARIANTS.map((option) => (
                  <option key={option} value={option}>
                    {TOAST_VARIANT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'toast-message',
            label: 'Message',
            control: (
              <input
                id="toast-message"
                className="ui-field-control"
                type="text"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            ),
          },
          {
            id: 'toast-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="toast-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedVariant, message],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Toast"
        description="8px padding, 8px radius, 16px status icon, 12/20 message. Info, success, warning, and danger keep their pastel fills on dark surfaces."
        stageClassName="ui-preview__stage--wide toast-showcase__stage-wrap"
      >
        <div
          className={`toast-showcase__sheet${
            darkMode ? ' toast-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              All four states stacked. Edit the message in the properties panel
              to update every toast together.
            </p>
            <div
              className={`toast-showcase__panel toast-showcase__panel--live${
                darkMode ? ' toast-showcase__panel--dark' : ''
              }`}
            >
              {TOAST_VARIANTS.map((option) => (
                <div
                  key={option}
                  ref={(element) => {
                    rowRefs.current[option] = element
                  }}
                  className={`toast-showcase__row${
                    option === highlightedVariant
                      ? ' toast-showcase__row--selected'
                      : ''
                  }`}
                >
                  <span className="toast-showcase__row-label">
                    {TOAST_VARIANT_LABELS[option]}
                  </span>
                  <Toast variant={option} message={message} />
                </div>
              ))}
            </div>
          </section>

          <section className="toast-showcase__matrix">
            <h4 className="toast-showcase__section-title">Light and dark</h4>
            <p className="ui-showcase-live__note">
              One toast frame. Dark is a `blue-900` canvas; the chips stay
              pastel.
            </p>
            <div className="toast-showcase__panel">
              {TOAST_VARIANTS.map((option) => (
                <Toast
                  key={`light-${option}`}
                  variant={option}
                  message={message}
                  previewVariant={option}
                />
              ))}
            </div>
            <div className="toast-showcase__panel toast-showcase__panel--dark">
              {TOAST_VARIANTS.map((option) => (
                <Toast
                  key={`dark-${option}`}
                  variant={option}
                  message={message}
                  previewVariant={option}
                />
              ))}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Toast" sections={propertySections} />
    </>
  )
}

export default ToastShowcase
